import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

export type RetiredAppSubdomain = {
  label: string;
  claimedAt: string;
};

export type OrgAppSubdomainData = {
  /** Active label, or null when the org is on the shared app host. */
  current: string | null;
  /** Always present — the org's vanity URL, or the shared origin when `current` is null. */
  appUrl: string;
  /**
   * Labels this org previously held. Retired labels are never released back
   * to the pool: a browser can hold a `__Host-` cookie scoped to the old
   * hostname long after a rename, and releasing the label would let a
   * different org start receiving those cookies. So the old host keeps
   * redirecting to the current one forever, and the list only grows.
   */
  retired: RetiredAppSubdomain[];
};

/** Any authenticated org member can read the org's app subdomain state. */
export function useOrgAppSubdomain() {
  return useQuery({
    queryKey: ['org-app-subdomain'],
    queryFn: async (): Promise<{ ok: boolean; data: OrgAppSubdomainData }> => {
      const res = await fetch(`${API_URL}/org/app-subdomain`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch app subdomain');
      return res.json();
    },
  });
}

/**
 * Setting/clearing the label is superadmin-only server-side (org admins get
 * read-only) — this mutation is only ever wired up behind an `isSuperadmin`
 * check in the UI.
 */
export function useSetOrgAppSubdomain(orgId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (appSubdomain: string | null) => {
      // Fail loudly rather than interpolating an empty string. `/organizations//`
      // collapses to a double slash, which nginx rewrites into something that
      // surfaces as an opaque 400 — an error nobody can act on, standing in for
      // a state that should be impossible (this hook is only reachable from an
      // org-scoped settings route).
      if (!orgId) {
        throw new Error('Cannot set the workspace address without an organization');
      }
      const res = await fetch(`${API_URL}/superadmin/organizations/${orgId}/app-subdomain`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ appSubdomain }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update app subdomain');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-app-subdomain'] });
      // `/me` carries `appSubdomain` on every org in `organizations[]`, and the
      // org switcher builds its cross-host URL from that copy. Leaving it stale
      // means switching INTO this org opens the address it used to have — the
      // exact 404/409 bounce the org-aware link work exists to prevent.
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}
